import React, { useState, useRef } from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import styles from "@modules/setting/userManagement/addingUser/addingUser.module.css";
import { useNavigate, useParams } from "react-router-dom";
import Message from "@shared/message/Message.tsx";
import {
  capitalizeFirstLetter,
  CommuneDTO,
  DistrictDTO,
  FokontanyDTO,
  RegionDTO,
  searchAutoComplete,
} from "@utils/appUtils.ts";
import { ProfilDTO } from "../profil.model.ts";
import { getPhoneMaxLength, UserPostDTO } from "../user.model.ts";
import { AutoComplete, AutoCompleteChangeEvent } from "primereact/autocomplete";
import { useAppDispatch, useAppSelector } from "@core/store/hook.ts";
import {
  editUser,
  getProfileList,
  getUserById,
  saveUser,
} from "@core/actions/usersActions.ts";
import {
  loadingEditUser,
  loadingUserDetails,
  profilResponse,
  saveLoader,
  userDetails,
} from "@core/slice/UsersSlice.ts";
import {
  communeList,
  districtList,
  fokontanyList,
  loadingDistrict,
  loadingLocalisation,
  loadingCommune,
  loadingFokotany,
  regionList,
} from "@core/slice/LocalisationSlice.ts";
import { ErrorApi, FetchStatusType } from "@core/modeles/model.ts";
import {
  getAllRegions,
  getCommuneByDistrict,
  getDistrictByRegion,
  getFokontanysByCommune,
} from "@core/actions/localisationAction.ts";
import { classNames } from "primereact/utils";
import Spinner from "@shared/Spinner.tsx";

const AddingUser: React.FC = () => {
  const params = useParams();
  const idUser = params.id;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const loading = useAppSelector(saveLoader);
  const loadingEdit = useAppSelector(loadingEditUser);
  const profiles = useAppSelector(profilResponse);
  const dataRegion = useAppSelector(regionList);
  const dataDistrict = useAppSelector(districtList);
  const dataCommune = useAppSelector(communeList);
  const dataFokontany = useAppSelector(fokontanyList);
  const userUpdatedDetail = useAppSelector(userDetails);
  const loadingDataEdit = useAppSelector(loadingUserDetails);
  const loadingRegion = useAppSelector(loadingLocalisation);
  const districtLoader = useAppSelector(loadingDistrict);
  const communeLoader = useAppSelector(loadingCommune);
  const fokotanyLoader = useAppSelector(loadingFokotany);

  const [formData, setFormData] = useState({
    lastname: "",
    firstname: "",
    username: "",
    password: "",
    email: "",
    phonenumber: "",
    address: "",
    roles: null as ProfilDTO | null,
    access: null,
    fokontanyId: 0,
    region: 0,
  });
  const [errors, setErrors] = useState({
    lastname: "",
    firstName: "",
    username: "",
    password: "",
    email: "",
    role: "",
    phonenumber: "",
    region: "",
    district: "",
    commune: "",
    fokotany: "",
  });

  const [filteredRegions, setFilteredRegions] = useState<RegionDTO[]>([]);
  const [filteredDistricts, setFilteredDistricts] = useState<DistrictDTO[]>([]);
  const [filteredCommunes, setFilteredCommunes] = useState<CommuneDTO[]>([]);
  const [filteredFokontanys, setFilteredFokontanys] = useState<FokontanyDTO[]>(
    []
  );

  const [selectedRegion, setSelectedRegion] = useState<RegionDTO | null>(null);
  const [selectedDistrict, setSelectedDistrict] = useState<DistrictDTO | null>(
    null
  );
  const [selectedCommune, setSelectedCommune] = useState<CommuneDTO | null>(
    null
  );
  const [selectedFokontany, setSelectedFokontany] =
    useState<FokontanyDTO | null>(null);

  const [open, setOpen] = useState(false);
  const [messageInfo, setMessageInfo] = useState<{
    severity: "success" | "info" | "warn" | "error";
    summary: string;
    detail: string;
  }>({
    severity: "success",
    summary: "",
    detail: "",
  });

  const autoCompleteRefs = useRef<{ [key: string]: any }>({
    region: null,
    district: null,
    commune: null,
    fokontany: null,
  });

  const showSuccessMessage = () => {
    setMessageInfo({
      severity: "success",
      summary: "Succès",
      detail: idUser
        ? "Modification utilisateur réussie!"
        : "Enregistrement utilisateur réussi",
    });
    setOpen(true);
  };

  const showErrorMessage = (error: ErrorApi) => {
    setMessageInfo({
      severity: "error",
      summary: "Erreur",
      detail: error.message,
    });
    setOpen(true);
  };

  React.useEffect(() => {
    setFilteredRegions(dataRegion);
  }, [dataRegion]);

  React.useEffect(() => {
    dispatch(
      getAllRegions({
        page: 1,
        size: 22,
      })
    );
    dispatch(
      getProfileList({
        page: 1,
        size: 1000,
        search: "",
        sortBy: "desc",
        sortDir: "",
      })
    );
  }, [dispatch]);

  React.useEffect(() => {
    if (userUpdatedDetail) {
      const roles =
        profiles &&
        profiles.data.find(
          (profil) => profil.id === userUpdatedDetail.profil.profilId?.value
        );
      const district = {
        id: userUpdatedDetail.fokontany.municipality?.district.id ?? 0,
        code: userUpdatedDetail.fokontany.municipality?.district.code ?? "",
        name: userUpdatedDetail.fokontany.municipality?.district.name ?? "",
      };
      const commune = {
        id: userUpdatedDetail.fokontany.municipality?.id ?? 0,
        name: userUpdatedDetail.fokontany.municipality?.name ?? "",
        code: userUpdatedDetail.fokontany.municipality?.code ?? "",
      };
      const fokotany = {
        id: userUpdatedDetail.fokontany.id,
        name: userUpdatedDetail.fokontany.name,
        code: userUpdatedDetail.fokontany.code,
      };
      setFormData({
        lastname: userUpdatedDetail.lastName || "",
        firstname: userUpdatedDetail.firstName || "",
        username: userUpdatedDetail.username || "",
        password: "",
        email: userUpdatedDetail.email || "",
        phonenumber: userUpdatedDetail.telephone || "",
        address: userUpdatedDetail.address || "",
        roles: roles ?? null,
        access: null,
        fokontanyId: userUpdatedDetail?.fokontany.id || 0,
        region: userUpdatedDetail.region?.id ?? 0,
      });
      setSelectedRegion(
        userUpdatedDetail.fokontany.municipality?.district.region ?? null
      );
      setSelectedDistrict(district ?? null);
      setSelectedCommune(commune ?? null);
      setSelectedFokontany(fokotany);
      setFormData((prevForm) => ({
        ...prevForm,
        region:
          userUpdatedDetail.fokontany.municipality?.district.region.id ?? 0,
      }));
    }
  }, [userUpdatedDetail, profiles]);

  React.useEffect(() => {
    if (idUser) {
      dispatch(getUserById(idUser));
    }
  }, [dispatch, idUser]);

  React.useEffect(() => {
    if (selectedRegion?.id) {
      const idRegion = selectedRegion.id;
      dispatch(
        getDistrictByRegion({
          page: 1,
          size: -1,
          region: idRegion.toString(),
        })
      );
    }
  }, [dispatch, selectedRegion]);

  React.useEffect(() => {
    if (selectedDistrict?.id) {
      const idDistrict = selectedDistrict.id;
      dispatch(
        getCommuneByDistrict({
          page: 1,
          size: -1,
          district: idDistrict.toString(),
        })
      );
    }
  }, [dispatch, selectedDistrict]);

  React.useEffect(() => {
    if (selectedCommune?.id) {
      const idCommune = selectedCommune.id;
      dispatch(
        getFokontanysByCommune({
          page: 1,
          size: -1,
          commune: idCommune.toString(),
        })
      );
    }
  }, [dispatch, selectedCommune]);

  const handleFocus = (
    type: string,
    data: any,
    setState: React.Dispatch<React.SetStateAction<any[]>>
  ) => {
    if (data.length > 0) {
      setState(data);
    }

    const ref = autoCompleteRefs.current[type];
    if (ref) {
      ref.show();
    }
  };

  // Mise à jour des districts en fonction de la région sélectionnée
  const handleChangeRegion = (e: AutoCompleteChangeEvent) => {
    const selectedData = e.value;
    setSelectedRegion(selectedData);
    setSelectedDistrict(null);
    setSelectedCommune(null);
    setSelectedFokontany(null);
    setFormData((prevForm) => ({
      ...prevForm,
      region: e.value.id,
    }));
    setErrors((prevErrors) => ({
      ...prevErrors,
      region: "",
    }));
  };

  // Mise à jour des communes en fonction du district sélectionné
  const handleChangeDistrict = (e: any) => {
    const selectedDistrict = e.value;
    setSelectedDistrict(selectedDistrict);
    setSelectedCommune(null);
    setSelectedFokontany(null);
    setErrors((prevErrors) => ({
      ...prevErrors,
      district: "",
    }));
  };

  // Mise à jour des fokontany en fonction de la commune sélectionnée
  const handleChangeCommune = (e: any) => {
    const selectedCommuneCode = e.value;
    setSelectedCommune(selectedCommuneCode);
    setSelectedFokontany(null);
    setErrors((prevErrors) => ({
      ...prevErrors,
      commune: "",
    }));
  };

  const containsNoNumbers = (text: string) => {
    return /^[^\d]+$/.test(text);
  };

  const handleChangeFokontany = (e: DropdownChangeEvent) => {
    setSelectedFokontany(e.value);
    setFormData((prevForm) => ({
      ...prevForm,
      fokontanyId: e.value.id,
    }));
    setErrors((prevErrors) => ({
      ...prevErrors,
      fokotany: "",
    }));
  };

  function handleCanceled() {
    navigate("/setting/list-user");
  }

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | any>
  ) => {
    const { name, value } = e.target;
    let transformedValue = value;
    let updatedErrors = { ...errors };

    switch (name) {
      case "lastname":
        transformedValue = value.toUpperCase();
        updatedErrors.lastname = "";
        break;

      case "firstname":
        transformedValue = capitalizeFirstLetter(value);
        updatedErrors.firstName = "";
        break;
      case "username":
        updatedErrors.username = "";
        break;

      case "email":
        updatedErrors.email = "";
        break;

      case "phonenumber":
        updatedErrors.phonenumber = "";

        break;

      case "password":
        if (
          (value.length > 0 && value.length < 6) ||
          containsNoNumbers(value)
        ) {
          updatedErrors.password =
            "Le mot de passe doit contenir au moins 6 caractères et au moins un chiffre.";
        } else {
          updatedErrors.password = "";
        }
        break;

      default:
        break;
    }

    setErrors(updatedErrors);
    setFormData({
      ...formData,
      [name]: transformedValue,
    });
  };

  const handleBlurPhone = (e: React.FocusEvent<HTMLInputElement>) => {
    let updatedErrors = { ...errors };
    const value = e.target.value.replace(/\s/g, "");

    const localPhoneRegex = /^(032|033|034|038|037|020)\d{7}$/;
    const internationalPhoneRegex = /^\+261(32|33|34|38|37|20)\d{7}$/;

    if (
      !localPhoneRegex.test(value) &&
      !internationalPhoneRegex.test(value) &&
      value !== ""
    ) {
      updatedErrors.phonenumber =
        "Numéro valide: 032|033|034|038|037|020xxxxxxx  +261xxxxxxxxx (ex: 032xxxxxxx ou +26132xxxxxxx)";
    } else {
      updatedErrors.phonenumber = "";
    }

    setErrors(updatedErrors);
  };

  const emailRegex = /^[a-zA-Z0-9@.]+$/;
  const emailStructureRegex =
    /^[a-zA-Z0-9]+(\.[a-zA-Z0-9]+)*@[a-zA-Z0-9]+(\.[a-zA-Z0-9]+)+$/;

  const handleBlurEmail = (e: React.FocusEvent<HTMLInputElement>) => {
    let updatedErrors = { ...errors };
    const value = e.target.value;
    if (!emailRegex.test(value)) {
      updatedErrors.email = "Caractères spéciaux non autorisés dans l'email.";
    } else if (!emailStructureRegex.test(value)) {
      updatedErrors.email = "Format d'email invalide.";
    } else {
      updatedErrors.email = "";
    }

    setErrors(updatedErrors);
  };

  const handleBlurUsername = (e: React.FocusEvent<HTMLInputElement>) => {
    let updatedErrors = { ...errors };
    const value = e.target.value;
    if (/\s/.test(value)) {
      updatedErrors.username = "Le nom d'utilisateur ne doit pas contenir d'espaces.";
    } else {
      updatedErrors.username = "";
    }

    setErrors(updatedErrors);
  };

  function handleChangeRole(e: DropdownChangeEvent) {
    setErrors({
      ...errors,
      role: "",
    });
    setFormData({ ...formData, roles: e.value });
  }

  const nameRegex = /^[\p{L}\s-]+$/u;

  function handleSaveUser() {
    let formErrors = { ...errors };
    if (formData.lastname.trim() === "") {
      formErrors.lastname = "Veuillez remplir le champ Nom";
    } else if (!nameRegex.test(formData.lastname)) {
      formErrors.lastname = "Le nom ne doit contenir que des lettres";
    } else {
      formErrors.lastname = "";
    }
    if (formData.firstname.trim() !== "") {
      if (!nameRegex.test(formData.firstname)) {
        formErrors.firstName = "Le prénom ne doit contenir que des lettres";
      } else {
        formErrors.firstName = "";
      }
    }
    if (formData.username.trim() === "") {
      formErrors.username = "Veuillez remplir le champ Nom d'utilisateur";
    } else if (/\s/.test(formData.username.trim())) {
      formErrors.username = "Le nom d'utilisateur ne doit pas contenir d'espaces.";
    } else {
      formErrors.username = "";
    }
    if (formData.password.trim() === "") {
      formErrors.password = "Veuillez remplir le champ Mot de passe";
    } else if (
      formData.password.trim() !== "" &&
      containsNoNumbers(formData.password.trim())
    ) {
      formErrors.password = "Veuillez ajouter au moins un chiffre";
    } else {
      formErrors.password = "";
    }
    if (formData.email.trim() === "") {
      formErrors.email = "Veuillez remplir le champ Email";
    } else if (!emailRegex.test(formData.email.trim())) {
      formErrors.email = "Adresse email invalide";
    } else formErrors.email = "";

    if (formData.roles === null) {
      formErrors.role = "Veuillez choisir le rôle";
    } else {
      formErrors.role = "";
    }

    if (selectedRegion === null) {
      formErrors.region = "Veuillez choisir la région";
    } else {
      formErrors.region = "";
    }

    if (selectedDistrict === null) {
      formErrors.district = "Veuillez choisir le district";
    } else {
      formErrors.district = "";
    }
    if (selectedCommune === null) {
      formErrors.commune = "Veuillez choisir la commune";
    } else {
      formErrors.commune = "";
    }
    if (selectedFokontany === null) {
      formErrors.fokotany = "Veuillez choisir la région";
    } else {
      formErrors.fokotany = "";
    }

    setErrors(formErrors);

    if (
      !formErrors.lastname &&
      (idUser ? true : !formErrors.password) &&
      !formErrors.email &&
      !formErrors.firstName &&
      !formErrors.username &&
      !formErrors.role &&
      !formErrors.phonenumber &&
      !formErrors.commune &&
      !formErrors.district &&
      !formErrors.region &&
      !formErrors.fokotany
    ) {
      const userPostData: UserPostDTO = {
        firstName: formData.firstname.trim(),
        lastName: formData.lastname.trim(),
        username: formData.username.trim(),
        email: formData.email.trim(),
        password: formData.password.trim(),
        telephone: formData.phonenumber.trim(),
        address: formData.address,
        status: "ACTIVE",
        profil: formData.roles?.id ?? "",
        fokontany: formData.fokontanyId,
        region: formData.region,
      };
      setOpen(false);
      const { password, ...userDataWithoutPassword } = userPostData;
      if (idUser && userUpdatedDetail) {
        dispatch(
          editUser({
            filter: {
              ...userDataWithoutPassword,
              id: idUser,
              uuid: idUser,
            },
            onSuccess: () => {
              showSuccessMessage();
              setTimeout(() => {
                setOpen(false);
                navigate("/setting/list-user");
              }, 2000);
            },
            onError: (error) => {
              showErrorMessage(error);
            },
          })
        );
      } else {
        dispatch(
          saveUser({
            filter: userPostData,
            onSuccess: () => {
              showSuccessMessage();
              setTimeout(() => {
                setOpen(false);
                navigate("/setting/list-user");
              }, 2000);
            },
            onError: (error) => {
              showErrorMessage(error);
            },
          })
        );
      }
    }
  }

  return (
    <>
      <Message
        open={open}
        severity={messageInfo.severity}
        summary={messageInfo.summary}
        detail={messageInfo.detail}
      />
      <div className="container flex justify-content-center py-auto">
        <div className="bg-white rounded-lg lg:w-1/2 sm:w-2/3 w-full my-auto gap-[20px] column p-8 ">
          <div className={styles.title}>
            {idUser ? "Modifier un utilisateur" : "Ajouter un utilisateur"}
          </div>

          {loadingDataEdit === "loading" ? (
            <div className="flex justify-center py-96">
              <Spinner />
            </div>
          ) : (
            <>
              <div className="column flex flex-column gap-[3px] ">
                <div className="flex flex-wrap gap-3">
                  <div className="flex-1 gap1">
                    <label>Nom (Obligatoire)</label>
                    <InputText
                      name="lastname"
                      value={formData.lastname}
                      onChange={handleInputChange}
                      className="w-full"
                      maxLength={255}
                    />

                    {errors.lastname && (
                      <small className="text-red-600">{errors.lastname}</small>
                    )}
                  </div>

                  <div className="flex-1 gap-1">
                    <label>Prénom</label>
                    <InputText
                      name="firstname"
                      value={formData.firstname}
                      onChange={handleInputChange}
                      className="w-full"
                      maxLength={255}
                    />

                    {errors.firstName && (
                      <small className="text-red-600">{errors.firstName}</small>
                    )}
                  </div>
                </div>
                <div className="column flex flex-column gap-[3px] ">
                  <div className="flex-1 gap-1">
                    <label>Nom d'utilisateur (Obligatoire)</label>
                    <InputText
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      onBlur={handleBlurUsername}
                      className="w-full"
                      maxLength={255}
                    />

                    {errors.username && (
                      <small className="text-red-600">{errors.username}</small>
                    )}
                  </div>
                </div>

                <div className="flex flex-wrap gap-3 mb-3">
                  <div className="flex-1 gap-1">
                    <label>Email (Obligatoire)</label>
                    <InputText
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full"
                      onBlur={handleBlurEmail}
                      maxLength={255}
                    />
                    {errors.email && (
                      <small className="text-red-600">{errors.email}</small>
                    )}
                  </div>

                  {!userUpdatedDetail && (
                    <div className="flex-1 gap-1">
                      <label>Mot de passe (Obligatoire)</label>
                      <InputText
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        className="w-full"
                        maxLength={255}
                      />

                      {errors.password && (
                        <small className="text-red-600">
                          {errors.password}
                        </small>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-3">
                  <div className="flex-1 gap-1">
                    <label>Rôles (Obligatoire)</label>
                    <Dropdown
                      name="role"
                      value={formData.roles}
                      options={profiles?.data ?? []}
                      optionLabel="name"
                      className="w-full"
                      placeholder="Sélectionner un rôle"
                      onChange={(e) => handleChangeRole(e)}
                    />

                    {errors.role && (
                      <small className="text-red-600">{errors.role}</small>
                    )}
                  </div>
                </div>

                <div className="flex flex-wrap mt-3">
                  <div className="column gap-1 w-full">
                    <label>Téléphone</label>
                    <InputText
                      name="phonenumber"
                      id="phonenumber"
                      value={formData.phonenumber}
                      onChange={handleInputChange}
                      className="w-full"
                      onBlur={handleBlurPhone}
                      maxLength={getPhoneMaxLength(formData.phonenumber)}
                    />

                    {errors.phonenumber && (
                      <small className="text-red-600">
                        {errors.phonenumber}
                      </small>
                    )}
                  </div>
                </div>

                <div className="flex flex-wrap mt-3">
                  <div className="column gap-1 w-full">
                    <label>Adresse</label>
                    <InputText
                      name="address"
                      type="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      className="w-full"
                      maxLength={255}
                    />
                  </div>
                </div>

                <div className="flex flex-wrap mt-3">
                  <div className="column gap-1 w-full">
                    <label>Région (obligatoire)</label>
                    <AutoComplete
                      ref={(el) => (autoCompleteRefs.current.region = el)}
                      value={selectedRegion}
                      className="w-full"
                      suggestions={filteredRegions}
                      completeMethod={(e) =>
                        searchAutoComplete(e, dataRegion, setFilteredRegions)
                      }
                      onChange={handleChangeRegion}
                      onFocus={() =>
                        handleFocus("region", dataRegion, setFilteredRegions)
                      }
                      dropdown
                      field="name"
                      placeholder="---Région---"
                      showEmptyMessage={
                        loadingRegion === FetchStatusType.LOADING ||
                        loadingRegion === FetchStatusType.FAILED
                      }
                      emptyMessage={
                        loadingRegion === FetchStatusType.FAILED
                          ? "Erreur de chargement"
                          : "Chargement en cours"
                      }
                    />
                  </div>
                  {errors.region && (
                    <small className="text-red-600">{errors.region}</small>
                  )}
                </div>

                <div className="flex flex-wrap mt-3">
                  <div className="column gap-1 w-full">
                    <label>District (obligatoire)</label>

                    <AutoComplete
                      ref={(el) => (autoCompleteRefs.current.district = el)}
                      value={selectedDistrict}
                      className="w-full"
                      suggestions={filteredDistricts}
                      completeMethod={(e) =>
                        searchAutoComplete(
                          e,
                          dataDistrict,
                          setFilteredDistricts
                        )
                      }
                      onChange={handleChangeDistrict}
                      onFocus={() =>
                        handleFocus(
                          "district",
                          dataDistrict,
                          setFilteredDistricts
                        )
                      }
                      dropdown
                      field="name"
                      placeholder="---District---"
                      showEmptyMessage={
                        districtLoader === FetchStatusType.LOADING ||
                        districtLoader === FetchStatusType.FAILED
                      }
                      emptyMessage={
                        districtLoader === FetchStatusType.FAILED
                          ? "Erreur de chargement"
                          : "Chargement en cours"
                      }
                      dropdownIcon={
                        districtLoader === FetchStatusType.LOADING ? (
                          <i className="pi pi-spin pi-spinner" />
                        ) : (
                          <i
                            className="pi pi-angle-down"
                            style={{ fontSize: "1.2rem" }}
                          />
                        )
                      }
                      disabled={districtLoader === FetchStatusType.LOADING}
                    />
                  </div>

                  {errors.district && (
                    <small className="text-red-600">{errors.district}</small>
                  )}
                </div>

                <div className="flex flex-wrap mt-3">
                  <div className="column gap-1 w-full">
                    <label>Commune (obligatoire)</label>

                    <AutoComplete
                      ref={(el) => (autoCompleteRefs.current.commune = el)}
                      value={selectedCommune}
                      className="w-full"
                      suggestions={filteredCommunes}
                      completeMethod={(e) =>
                        searchAutoComplete(e, dataCommune, setFilteredCommunes)
                      }
                      onChange={handleChangeCommune}
                      onFocus={() =>
                        handleFocus("commune", dataCommune, setFilteredCommunes)
                      }
                      dropdown
                      field="name"
                      placeholder="---Commune---"
                      showEmptyMessage={
                        communeLoader === FetchStatusType.LOADING ||
                        communeLoader === FetchStatusType.FAILED
                      }
                      emptyMessage={
                        communeLoader === FetchStatusType.FAILED
                          ? "Erreur de chargement"
                          : "Chargement en cours"
                      }
                      dropdownIcon={
                        communeLoader === FetchStatusType.LOADING ? (
                          <i className="pi pi-spin pi-spinner" />
                        ) : (
                          <i
                            className="pi pi-angle-down"
                            style={{ fontSize: "1.2rem" }}
                          />
                        )
                      }
                      disabled={communeLoader === FetchStatusType.LOADING}
                    />
                  </div>
                  {errors.commune && (
                    <small className="text-red-600">{errors.commune}</small>
                  )}
                </div>

                <div className="flex flex-wrap mt-3">
                  <div className="column gap-1 w-full">
                    <label>Fokontany (obligatoire)</label>

                    <AutoComplete
                      ref={(el) => (autoCompleteRefs.current.fokontany = el)}
                      value={selectedFokontany}
                      className="w-full"
                      suggestions={filteredFokontanys}
                      completeMethod={(e) =>
                        searchAutoComplete(
                          e,
                          dataFokontany,
                          setFilteredFokontanys
                        )
                      }
                      onChange={handleChangeFokontany}
                      onFocus={() =>
                        handleFocus(
                          "fokontany",
                          dataFokontany,
                          setFilteredFokontanys
                        )
                      }
                      dropdown
                      field="name"
                      placeholder="---Fokontany---"
                      showEmptyMessage={
                        fokotanyLoader === FetchStatusType.LOADING ||
                        fokotanyLoader === FetchStatusType.FAILED
                      }
                      emptyMessage={
                        fokotanyLoader === FetchStatusType.FAILED
                          ? "Erreur de chargement"
                          : "Chargement en cours"
                      }
                      dropdownIcon={
                        fokotanyLoader === FetchStatusType.LOADING ? (
                          <i className="pi pi-spin pi-spinner" />
                        ) : (
                          <i
                            className="pi pi-angle-down"
                            style={{ fontSize: "1.2rem" }}
                          />
                        )
                      }
                      disabled={fokotanyLoader === FetchStatusType.LOADING}
                    />
                  </div>
                  {errors.fokotany && (
                    <small className="text-red-600">{errors.fokotany}</small>
                  )}
                </div>
              </div>

              <div className="flex pt-4 gap-3 justify-content-center">
                <Button
                  className={classNames("btn btn-secondary w-40", {
                    "btn-secondary-disabled":
                      loading === FetchStatusType.LOADING ||
                      loadingEdit === FetchStatusType.LOADING,
                  })}
                  onClick={handleCanceled}
                  disabled={
                    loading === FetchStatusType.LOADING ||
                    loadingEdit === FetchStatusType.LOADING
                  }
                >
                  Annuler
                </Button>
                <Button
                  className={classNames("btn btn-primary w-40", {
                    "btn-primary-disabled":
                      loading === FetchStatusType.LOADING ||
                      loadingEdit === FetchStatusType.LOADING,
                  })}
                  onClick={handleSaveUser}
                  disabled={
                    loading === FetchStatusType.LOADING ||
                    loadingEdit === FetchStatusType.LOADING
                  }
                >
                  {loading === FetchStatusType.LOADING ||
                    (loadingEdit === FetchStatusType.LOADING && (
                      <div className="mr-2 w-4 h-4 rounded-full animate-spin border-y-4 border-solid border-white border-t-transparent shadow-md pe-2"></div>
                    ))}
                  Enregistrer
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default AddingUser;
